import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ProductCardCallout from '~/gridv1_1/components/ProductCard/ProductCardCallout';
import {
  promoPriceHiddenPromoTypes,
  promoPriceNoStrikeThrough,
} from '~/shared/constants/products';
import { SecondarySites } from '~/shared/constants/secondarySites';
import { getColorFromAlias } from '~/shared/utils/getColorFromAlias';
import { getPromoPricingActiveAsset } from '~/shared/utils/getPromoPricingActiveAsset';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useAsset } from '~/techstyle-shared/react-assets';
import {
  ProductPrice as BaseProductPrice,
  useProductContext,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import {
  useIntl,
  Currency,
  FormattedMessage,
} from '~/techstyle-shared/react-intl';
import { useSession } from '~/techstyle-shared/redux-core';

import featureNames from '../../constants/featureFlags';
import { getPromoPricingUIAssetContainerName } from '../../utils/promoPricingUI';
import useEMPPricing from '../../utils/useEMPPricing';
import useGshop from '../../utils/useGshop';
import useHasFeature from '../../utils/useHasFeature';
import MemberCreditCallout from '../MemberCreditCallout';
import TypedText from '../TypedText';

const DynamicFormattedMessage = FormattedMessage;

const PromoMessage = styled.span`
  ${({ promoMessageStyles }) => promoMessageStyles};
  ${({ theme }) => theme.pdpStyles.upsellPromos};
  ${({ theme }) => theme.gridCellStyles.promoMessage};
`;

const PromoLabelFromCms = styled.span`
  ${({ theme }) => theme.promoMessage.overrideMarkupStylesFromCms};
`;

const ProductColor = styled(TypedText).attrs({
  type: 'header',
  as: 'p',
  size: '12',
})`
  line-height: 18px;
`;

const defaultPriceStyle = css`
  ${({ theme }) => theme.gridCellStyles.defaultPriceStyle}
`;

const vipPriceStyle = css`
  ${({ theme }) => theme.gridCellStyles.vipPriceStyles}
`;

const priceStyleFavorites = css`
  color: ${({ theme }) => theme.colors.textEmphasized};
  font-size: 16px;
  line-height: 1;
`;

const PriceContentWrapper = styled.div`
  ${({ theme }) => theme.gridCellStyles.priceContent}
  ${({ priceContentStyles }) => priceContentStyles};
`;

const ProductPrice = styled(BaseProductPrice)`
  font-size: 14px;
  font-style: normal;
  letter-spacing: ${-0.4 / 16}em;
  ${({ $productPriceComponentStyles }) => $productPriceComponentStyles};
`;

const defaultPromoPriceStyle = css`
  ${({ theme }) => theme.gridCellStyles.defaultPromoPriceStyle}
`;

const PriceOffer = styled.span`
  ${({ theme }) => theme.gridCellStyles.defaultPromoPriceStyle}
`;

const PriceOfferOr = styled.span`
  color: ${({ theme }) => theme.colors.priceOfferOr};
`;

const PriceContentLoadingPlaceholder = styled.div`
  height: 20px;
`;

const OutletProductPrice = styled(Currency)`
  margin-right: 5px;
  font-size: 16px;
  font-weight: 600;
`;

const VariantProductCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  padding-top: 16px;
`;

const VariantPricingAndCallout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
`;

const VariantHeaderAndPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const VariantPricing = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${({ $isMobile }) =>
    $isMobile &&
    `
    gap: 8px;
    align-self: stretch;
  `}
`;

const VariantLabel = styled.div`
  align-self: stretch;
  color: ${({ theme }) => theme.colors.pricingUiTestBoldLabel};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  text-transform: capitalize;
`;

const VariantBoldPrice = styled(Currency)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.pricingUiTestBoldLabel};
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: ${({ $isMobile }) => ($isMobile ? '18px' : '20px')};
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`;

const VariantStrikeThroughPrice = styled(Currency)`
  color: ${({ theme }) => theme.colors.pricingUiTestLabel};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
  text-decoration-line: line-through;
`;

const VariantPromo = styled.div`
  display: flex;
  padding: 8px 12px 10px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  background: ${({ theme }) => theme.colors.pricingUiTestPromoBox};
  ${({ $isMobile }) => $isMobile && 'align-self: stretch;'};
`;

const VariantPromoText = styled.div`
  display: flex;
  align-items: center;
  ${({ $isMobile }) => $isMobile && 'align-self: stretch;'};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
  color: ${({ theme }) => theme.colors.pricingUiTestPromoText};
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: normal;
  font-weight: 350;
  line-height: 16px;
`;

const VariantPromoOffer = styled.span`
  font-weight: 600;
  text-transform: uppercase;
`;

const VariantCalloutStyle = css`
  font-weight: 350;
  color: ${({ theme }) => theme.colors.pricingUiTestLabel};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: normal;
  font-weight: 350;
  line-height: 12px;
  text-transform: uppercase;
`;

const MemberCreditOnlyStrikeThroughPrice = styled(VariantStrikeThroughPrice)`
  margin-left: 5px;
`;

function PriceContent({ product, priceContentStyles, children }) {
  // using retail price to determine whether pricing has loaded. This definition may need to change in the future
  const isPricingLoaded = Boolean(product?.retailUnitPrice);
  return (
    <PriceContentWrapper priceContentStyles={priceContentStyles}>
      {isPricingLoaded ? children : <PriceContentLoadingPlaceholder />}
    </PriceContentWrapper>
  );
}

PriceContent.propTypes = {
  children: PropTypes.node,
  priceContentStyles: PropTypes.any,
  product: PropTypes.object,
};

const PriceDetailsUIVariant = ({ isNewPricingUiEnabled, ...props }) => {
  if (isNewPricingUiEnabled) {
    return <PricingUIVariant1 {...props} />;
  }
  return <PricingUIControl {...props} />;
};
PriceDetailsUIVariant.propTypes = {
  color: PropTypes.string,
  favorites: PropTypes.bool,
  getPriceStyle: PropTypes.func,
  getRetailPriceStyle: PropTypes.func,
  getShowRetailPrice: PropTypes.func,
  getVipPriceStyle: PropTypes.func,
  gridLabel: PropTypes.string,
  hasPromo: PropTypes.bool,
  htmlText: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  isNewPricingUiEnabled: PropTypes.bool,
  isOutlet: PropTypes.bool,
  isPromoFromCMS: PropTypes.bool,
  priceContentStyles: PropTypes.any,
  product: PropTypes.object,
  productPriceComponentStyles: PropTypes.any,
  promoCode: PropTypes.string,
  promoLabel: PropTypes.string,
  promoMessageStyles: PropTypes.any,
  promoPrice: PropTypes.number,
  promoPriceStyle: PropTypes.any,
  showGridLabel: PropTypes.bool,
  showMemberCredits: PropTypes.bool,
  showPromoPrice: PropTypes.any,
  showStrikeThrough: PropTypes.bool,
  showVipPrice: PropTypes.bool,
  tokenValue: PropTypes.number,
};

const PricingUIVariant1 = ({
  product,
  gridLabel: bogoLabel,
  promoPrice,
  promoCode,
  promoLabel: discountLabel,
  isOutlet,
}) => {
  const { isVip } = useMembership();
  const { isMobile } = useBreakpoint();
  const { formatMessage } = useIntl();

  const hasDiscountPromo = Boolean(promoCode) && typeof promoCode === 'string';

  // With Pricing Service V3, both bogo promoLabel (gridLabel) and
  // discount promoLabel (promoLabel) can have the "Limited Time Offer" prefix.
  // If they do, we want to remove it from gridLabel, so that it only appears once.
  const ltoPrefix = formatMessage({
    id: `grid_static.limited_time_offer`,
    defaultMessage: 'Limited Time Offer',
  });
  const discountLabelHasLtoPrefix =
    hasDiscountPromo &&
    discountLabel?.length > 0 &&
    discountLabel.toLowerCase().startsWith(ltoPrefix.toLowerCase());
  const bogoLabelHasLtoPrefix =
    bogoLabel?.length > 0 &&
    bogoLabel.toLowerCase().startsWith(ltoPrefix.toLowerCase());
  const bothHaveLtoPrefix = bogoLabelHasLtoPrefix && discountLabelHasLtoPrefix;
  const bogoLabelHasLtoColon =
    bothHaveLtoPrefix && bogoLabel.charAt(ltoPrefix.length) === ':';
  const parsedBogoLabel = bothHaveLtoPrefix
    ? bogoLabel.slice(ltoPrefix.length + (bogoLabelHasLtoColon ? 1 : 0)).trim()
    : bogoLabel;

  const showBogoLabel =
    !isOutlet && // outlet should not show promo labels
    parsedBogoLabel?.length > 0;

  // technically, this variant is intended to only ever be used for leads. But
  // in case the LD test is set up incorrectly, handle vip pricing.
  const basePrice = isVip
    ? product.pricing?.vip || product.defaultUnitPrice
    : product.pricing?.retail || product.retailUnitPrice;

  const outletPrice = !isNaN(product.outletUnitPrice)
    ? product.outletUnitPrice
    : product.warehouseUnitPrice;
  const discountPrice = isOutlet ? outletPrice : promoPrice;
  const hasDiscount = Boolean(discountPrice);

  return (
    <VariantProductCard $isMobile={isMobile}>
      <VariantPricingAndCallout>
        <VariantHeaderAndPrice>
          {hasDiscount && !isOutlet && (
            <VariantLabel>{discountLabel}</VariantLabel>
          )}
          <VariantPricing>
            <VariantBoldPrice
              amount={hasDiscount ? discountPrice : basePrice}
            />
            {hasDiscount && <VariantStrikeThroughPrice amount={basePrice} />}
          </VariantPricing>
        </VariantHeaderAndPrice>
        <ProductCardCallout
          product={product}
          calloutStyle={VariantCalloutStyle}
        />
      </VariantPricingAndCallout>
      {showBogoLabel && (
        <VariantPromo>
          <VariantPromoText>
            <VariantPromoOffer>{parsedBogoLabel}</VariantPromoOffer>
          </VariantPromoText>
        </VariantPromo>
      )}
    </VariantProductCard>
  );
};
PricingUIVariant1.propTypes = {
  gridLabel: PropTypes.string,
  isOutlet: PropTypes.bool,
  product: PropTypes.object,
  promoCode: PropTypes.string,
  promoLabel: PropTypes.string,
  promoPrice: PropTypes.number,
};

const PricingUIControl = ({
  hasPromo,
  promoMessageStyles,
  isPromoFromCMS,
  htmlText,
  promoCode,
  promoLabel,
  product,
  priceContentStyles,
  favorites,
  color,
  promoPriceStyle,
  showPromoPrice,
  showStrikeThrough,
  showVipPrice,
  productPriceComponentStyles,
  showGridLabel,
  gridLabel,
  isLoggedIn,
  showMemberCredits,
  tokenValue,
  getPriceStyle,
  getVipPriceStyle,
  getRetailPriceStyle,
  getShowRetailPrice,
}) => {
  const { formatMessage } = useIntl();

  // With Pricing Service V3, both bogo promoLabel (gridLabel) and
  // discount promoLabel (promoLabel) can have the "Limited Time Offer" prefix.
  // If they do, we want to remove it from gridLabel, so that it only appears once.
  const ltoPrefix = formatMessage({
    id: `grid_static.limited_time_offer`,
    defaultMessage: 'Limited Time Offer',
  });
  const promoLabelHasLtoPrefix =
    hasPromo &&
    promoLabel &&
    promoLabel.toLowerCase().startsWith(ltoPrefix.toLowerCase());
  const gridLabelHasLtoPrefix =
    showGridLabel &&
    gridLabel &&
    gridLabel.toLowerCase().startsWith(ltoPrefix.toLowerCase());
  const bothHaveLtoPrefix = gridLabelHasLtoPrefix && promoLabelHasLtoPrefix;
  const gridLabelHasLtoColon =
    bothHaveLtoPrefix && gridLabel.charAt(ltoPrefix.length) === ':';
  const parsedGridLabel = bothHaveLtoPrefix
    ? gridLabel.slice(ltoPrefix.length + (gridLabelHasLtoColon ? 1 : 0)).trim()
    : gridLabel;

  return (
    <>
      {hasPromo && (
        <PromoMessage
          data-testid="grid-promo-message"
          promoMessageStyles={promoMessageStyles}
        >
          {isPromoFromCMS ? (
            <PromoLabelFromCms dangerouslySetInnerHTML={{ __html: htmlText }} />
          ) : (
            promoLabel || (
              // fall back to using RB translation. This should hopefully be temporary until
              // we update ALL CMS promos to having their pdp_label set properly.
              <DynamicFormattedMessage
                id={`grid.callout_${promoCode.toLowerCase()}`}
                defaultMessage={promoLabel}
              />
            )
          )}
        </PromoMessage>
      )}
      <PriceContent product={product} priceContentStyles={priceContentStyles}>
        {favorites && color && (
          <ProductColor>
            <FormattedMessage
              id="grid.color"
              defaultMessage="Color: {color}"
              values={{ color }}
            />
          </ProductColor>
        )}
        <ProductPrice
          {...(hasPromo
            ? {
                promoPriceStyle,
                showPromoPrice,
                strikeRegularPrice: showStrikeThrough,
              }
            : {
                showPromoPrice: false,
              })}
          priceStyle={getPriceStyle()}
          vipPriceStyle={getVipPriceStyle()}
          retailPriceStyle={getRetailPriceStyle()}
          showRetailPrice={getShowRetailPrice()}
          showVipPrice={showVipPrice}
          $productPriceComponentStyles={productPriceComponentStyles}
        />{' '}
        {showGridLabel && parsedGridLabel.length > 0 && (
          <>
            <PriceOfferOr data-testid="grid-price-offer-or">
              <FormattedMessage
                id="global_cta.outfit_credit_or"
                defaultMessage="or"
              />{' '}
            </PriceOfferOr>
            <PriceOffer>{parsedGridLabel}</PriceOffer>
          </>
        )}
        {isLoggedIn && showMemberCredits && (
          <MemberCreditCallout
            data-autotag="grid-credit-price"
            product={product}
            tokenValue={tokenValue}
          />
        )}
        <ProductCardCallout product={product} />
      </PriceContent>
    </>
  );
};
PricingUIControl.propTypes = {
  color: PropTypes.string,
  favorites: PropTypes.bool,
  getPriceStyle: PropTypes.func,
  getRetailPriceStyle: PropTypes.func,
  getShowRetailPrice: PropTypes.func,
  getVipPriceStyle: PropTypes.func,
  gridLabel: PropTypes.string,
  hasPromo: PropTypes.bool,
  htmlText: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  isPromoFromCMS: PropTypes.bool,
  priceContentStyles: PropTypes.any,
  product: PropTypes.object,
  productPriceComponentStyles: PropTypes.any,
  promoCode: PropTypes.string,
  promoLabel: PropTypes.string,
  promoMessageStyles: PropTypes.any,
  promoPriceStyle: PropTypes.any,
  showGridLabel: PropTypes.bool,
  showMemberCredits: PropTypes.bool,
  showPromoPrice: PropTypes.any,
  showStrikeThrough: PropTypes.bool,
  showVipPrice: PropTypes.bool,
  tokenValue: PropTypes.number,
};

function ProductCellPriceDetails({
  product: productFromProps,
  priceStyle: priceStyleFromProps,
  isNewPricingUiEnabled,
  promoPriceStyle: promoPriceStyleFromProps,
  promoMessageStyles,
  priceContentStyles,
  productPriceComponentStyles,
  favorites,
}) {
  const { isLoggedIn } = useSession();
  const productFromContext = useProductContext();
  const intl = useIntl();
  const product = productFromProps || productFromContext;
  const { isVip } = useMembership();
  const { enabled: displayRetailPrice } = useHasFeature(
    featureNames.RETAILPRICETEST
  );
  const { data: pricingServiceV3FF } = useHasFeature(
    featureNames.PRICING_SERVICE_V3
  );

  // Some pages are not powered by pricing service. Checking if product has pricing
  // service sourced details, if not, then we override pricingServiceV3 back to false.
  const isPricingServiceV3Enabled =
    pricingServiceV3FF && Boolean(product.pricing?.bogoDetails);

  const {
    promoCode: promoCodeFromPromos,
    promoLabel: promoLabelFromPromos,
    promoTypeId: promoTypeIdFromPromos,
    promoPrice: promoPriceFromPromos,
  } = product?.promos || {};
  const {
    promoCode: promoCodeFromPricing,
    promoLabel: promoLabelFromPricing,
    promoTypeId: promoTypeIdFromPricing,
    promoPrice: promoPriceFromPricing,
  } = product?.pricing?.discountDetails || {};

  const promoCode = isPricingServiceV3Enabled
    ? promoCodeFromPricing
    : promoCodeFromPromos;
  const promoLabel = isPricingServiceV3Enabled
    ? promoLabelFromPricing
    : promoLabelFromPromos;
  const promoTypeId = isPricingServiceV3Enabled
    ? promoTypeIdFromPricing
    : promoTypeIdFromPromos;
  const promoPrice = isPricingServiceV3Enabled
    ? promoPriceFromPricing
    : promoPriceFromPromos;

  const hasPromo = Boolean(promoCode) && typeof promoCode === 'string';

  const useCustomVipGridPricingStyle = config.get(
    'public.brand.features.useCustomVipGridPricingStyle'
  );

  const shouldUseCustomVipPricing = useCustomVipGridPricingStyle && isVip;

  const showPromoPrice = hasPromo
    ? !promoPriceHiddenPromoTypes.has(promoTypeId)
    : undefined;
  const showStrikeThrough =
    !promoPriceNoStrikeThrough.has(promoTypeId) && !shouldUseCustomVipPricing;
  const { isTokenOnly, showMemberCredits, tokenValue } = useEMPPricing(product);
  const secondarySite = useSecondarySite();

  const promoPricingConfig = config.get('public.brand.promoPricingUI');
  const { enabled: isPrioritizedFeatureEnabled } = useHasFeature(
    featureNames.PRIORITIZED_PROMOS
  );
  const isPrioritizedEnabled =
    isPrioritizedFeatureEnabled && promoPricingConfig?.showPromoPricingOffer;
  const offerLabelContainerName = getPromoPricingUIAssetContainerName(
    promoPricingConfig,
    isVip
  );

  // Pre pricing service v3, promo details come from CMS when both bogo and the
  // regular discount promo types were present for product.
  const assetContainerOfferLabel = useAsset(offerLabelContainerName);
  const promoPricingActiveAsset = getPromoPricingActiveAsset({
    isPrioritizedEnabled,
    assetContainerOfferLabel,
    product,
  });
  const isPromoFromCMS =
    !isPricingServiceV3Enabled && Boolean(promoPricingActiveAsset);

  const cmsGridLabel =
    promoPricingActiveAsset?.options?.customVars?.gridLabel ||
    promoPricingActiveAsset?.options?.customVars?.overlayText ||
    promoPricingActiveAsset?.options?.customVars?.pdpLabel;
  // htmlText only used for cms promos
  const htmlText = promoPricingActiveAsset?.options?.htmlText;

  const hasBogoPromo =
    product.pricing?.bogoDetails?.promoCode ||
    product.pricing?.bogoDetails?.promoLabel;
  const pricingV3gridLabel =
    hasBogoPromo && isPrioritizedEnabled
      ? product.pricing.bogoDetails.promoLabel ||
        // fall back to using RB translation. This should hopefully be temporary until
        // we update ALL CMS promos to having their pdp_label set properly.
        intl.formatMessage({
          id: `grid.callout_${product.pricing.bogoDetails.promoCode.toLowerCase()}`,
          defaultMessage: product.pricing.bogoDetails.promoLabel,
        })
      : '';
  const gridLabel = isPricingServiceV3Enabled
    ? pricingV3gridLabel
    : cmsGridLabel;
  const showGridLabel = gridLabel?.length > 0;

  const priceStyle = priceStyleFromProps || defaultPriceStyle;
  const promoPriceStyle = favorites
    ? priceStyleFavorites
    : promoPriceStyleFromProps || defaultPromoPriceStyle;

  const color = product.color || getColorFromAlias(product.alias);

  const { isGshopActive, isGshopMSRPDiscountActive } = useGshop();
  const showVipPrice =
    !displayRetailPrice || (isGshopMSRPDiscountActive ? false : isGshopActive);

  const isOutlet = secondarySite === SecondarySites.OUTLET;
  const finalPrice = product.pricing?.final || product.defaultUnitPrice;

  if (isOutlet && !isNewPricingUiEnabled) {
    const outletPrice = !isNaN(product.outletUnitPrice)
      ? product.outletUnitPrice
      : product.warehouseUnitPrice;

    return (
      <PriceContent product={product} priceContentStyles={priceContentStyles}>
        <OutletProductPrice
          amount={outletPrice}
          data-autotag="product-outlet-price"
        />
        <ProductPrice
          {...{
            showPromoPrice: false,
            strikeRegularPrice: true,
          }}
          priceStyle={priceStyle}
          showRetailPrice
          showVipPrice={false}
          $productPriceComponentStyles={productPriceComponentStyles}
        />
      </PriceContent>
    );
  }

  const getPriceStyle = () => {
    // custom vip scenario
    if (shouldUseCustomVipPricing) {
      return null;
    }

    return !hasPromo && showVipPrice ? vipPriceStyle : priceStyle;
  };

  const getVipPriceStyle = () => {
    // custom vip scenario
    if (shouldUseCustomVipPricing) {
      if (hasPromo && gridLabel) {
        return promoPriceStyle;
      }

      if (!hasPromo && !gridLabel) {
        return promoPriceStyle;
      }

      if (hasPromo) {
        return null;
      }

      if (!gridLabel) {
        return null;
      }
    }

    return null;
  };

  const getRetailPriceStyle = () => {
    if (shouldUseCustomVipPricing) {
      return priceStyle;
    }

    return null;
  };

  const getShowRetailPrice = () => {
    // custom vip scenario where there is no promo code
    if (shouldUseCustomVipPricing && !hasPromo) {
      return true;
    }

    // custom vip scenario where there no asset with the promo
    if (shouldUseCustomVipPricing && !gridLabel) {
      return false;
    }

    return isGshopMSRPDiscountActive
      ? true
      : !showGridLabel && displayRetailPrice && !isGshopActive;
  };

  return (
    <>
      {isTokenOnly ? (
        <>
          <PriceContent product={product}>
            <MemberCreditCallout
              data-autotag="grid-credit-price"
              isTokenOnly
              product={product}
              tokenValue={tokenValue}
            />
            <MemberCreditOnlyStrikeThroughPrice amount={finalPrice} />
          </PriceContent>
        </>
      ) : (
        <PriceDetailsUIVariant
          isNewPricingUiEnabled={isNewPricingUiEnabled}
          hasPromo={hasPromo}
          promoMessageStyles={promoMessageStyles}
          isPromoFromCMS={isPromoFromCMS}
          htmlText={htmlText}
          promoCode={promoCode}
          promoLabel={promoLabel}
          product={product}
          priceContentStyles={priceContentStyles}
          favorites={favorites}
          color={color}
          promoPriceStyle={promoPriceStyle}
          promoPrice={promoPrice}
          showPromoPrice={showPromoPrice}
          showStrikeThrough={showStrikeThrough}
          showVipPrice={showVipPrice}
          productPriceComponentStyles={productPriceComponentStyles}
          showGridLabel={showGridLabel}
          gridLabel={gridLabel}
          isLoggedIn={isLoggedIn}
          showMemberCredits={showMemberCredits}
          tokenValue={tokenValue}
          getPriceStyle={getPriceStyle}
          getVipPriceStyle={getVipPriceStyle}
          getRetailPriceStyle={getRetailPriceStyle}
          getShowRetailPrice={getShowRetailPrice}
          isOutlet={isOutlet}
        />
      )}
    </>
  );
}

ProductCellPriceDetails.propTypes = {
  favorites: PropTypes.bool,
  isNewPricingUiEnabled: PropTypes.bool,
  priceContentStyles: PropTypes.any,
  priceStyle: PropTypes.any,
  product: PropTypes.object,
  productPriceComponentStyles: PropTypes.any,
  promoMessageStyles: PropTypes.any,
  promoPriceStyle: PropTypes.any,
};

export default ProductCellPriceDetails;
